/* ----------------------------------------------------------------------------
   M1 · codeData — field-defensive mapping of backend courier docs (camelCase
   OR snake_case keys) and agreement-timeline → badge derivation for one code.
   Timeline item: {agreement, expired, expiresInDays, ...}; a code is governed
   when an item has agreement.stage==='SIGNED' && status==='ACTIVE' and the
   agreement applies to the code (appliesToAll or linkedCpAccountCodes).
   ---------------------------------------------------------------------------- */

/** First non-null value among the given keys. */
export const pick = (doc, ...keys) => {
  for (let i = 0; i < keys.length; i++) {
    if (doc && doc[keys[i]] != null) return doc[keys[i]]
  }
  return undefined
}

const asBool = (v) => v === true || v === 'true' || v === 1 || v === '1'

/** Backend courier doc → flat row the table/drawers consume. */
export const normalizeCourier = (doc) => {
  const code = pick(doc, 'cpAccountCode', 'cp_account_code') || ''
  const stP = pick(doc, 'serviceTypePrimary', 'service_type_primary')
  const stS = pick(doc, 'serviceTypeSecondary', 'service_type_secondary')
  const isActive = asBool(pick(doc, 'isActive', 'is_active'))
  const shipType = pick(doc, 'supportedShipmentType', 'supported_shipment') || ''
  return {
    id: pick(doc, 'id', '_id'),
    code,
    display: pick(doc, 'name') || code,
    prozoId: pick(doc, 'prozoId', 'prozo_id') || '',
    /* Courier Code Display (phase 1): derived on the backend, null until parent + service type + min weight exist */
    logicalName: pick(doc, 'logicalName', 'logical_name') || '',
    priority: (() => { const p = pick(doc, 'priority'); return p == null || p === '' ? null : Number(p) })(),
    parent: pick(doc, 'parent') || '',
    parentId: pick(doc, 'parentId', 'parent_id') || null,
    serviceTypes: [stP, stS].filter(Boolean),
    type: pick(doc, 'type') || '',
    supportedShipmentType: shipType,
    vertical: shipType === 'B2B' ? 'PTL' : 'D2C',
    isActive,
    status: isActive ? 'Active' : 'Inactive',
    lastModified: pick(doc, 'lastModifiedDate', 'last_modified_date', 'modifiedDate', 'modified_date') || null,
    email: pick(doc, 'email') || '',
    raw: doc,
  }
}

/** Does one agreement govern this account code? */
const appliesTo = (a, row) => {
  if (pick(a, 'appliesToAll', 'applies_to_all')) return true
  const codes = pick(a, 'linkedCpAccountCodes', 'linked_cp_account_codes') || []
  if (row.code && codes.indexOf(row.code) >= 0) return true
  const ids = pick(a, 'linkedCpIds', 'linked_cp_ids') || []
  return row.id != null && ids.indexOf(String(row.id)) >= 0
}

/** Timeline items applicable to this code (any stage/status). */
export const itemsForCode = (items, row) =>
  (items || []).filter((it) => appliesTo((it && it.agreement) || {}, row))

/**
 * Badge derivation (TRD §M0.11) from the parent's agreement timeline.
 * Returns {key, tip, governing} — governing is the timeline ITEM (or null).
 */
export const codeAgreementInfo = (items, row) => {
  const mine = itemsForCode(items, row)
  let governing = null
  let pendingStage1 = false
  mine.forEach((it) => {
    const a = it.agreement || {}
    const stage = pick(a, 'stage')
    const status = pick(a, 'status')
    if (stage === 'SIGNED' && status === 'ACTIVE') {
      const from = new Date(pick(a, 'effectiveFrom', 'effective_from') || 0)
      const best = governing
        ? new Date(pick(governing.agreement, 'effectiveFrom', 'effective_from') || 0)
        : null
      if (!governing || from > best) governing = it
    } else if (stage === 'EMAIL_RECEIVED' && (status === 'ACTIVE' || status === 'PENDING_APPROVAL')) {
      pendingStage1 = true
    }
  })
  if (governing && governing.expired)
    return {key: 'expired', tip: 'The governing signed agreement has expired.', governing}
  if (governing && pendingStage1)
    return {
      key: 'addendum',
      tip: 'Signed agreement in force; a newer agreement is email-received and pending signature.',
      governing,
    }
  if (governing) return {key: 'signed', tip: 'Operating under the current signed agreement.', governing}
  if (pendingStage1)
    return {
      key: 'approvalpending',
      tip: 'Only a Stage 1 (email received) agreement exists — signed agreement pending.',
      governing: null,
    }
  return {key: 'norecord', tip: 'No agreement on file for this code.', governing: null}
}
